import {RichTextTool, toolsFromToolbarGroups} from '@app/_components/unified/sb-rich-text/sb-rich-text-tools';

/**
 * The widget texts' editors (share page, the project's widget page, the global widget editor):
 * an inline text (no paragraphs; Enter is a line break), bold, italic and underline as span
 * styles, underline on a link switching the link's own underline, new links opening in a new tab.
 * Those are set on each app-sb-rich-text (shape="inline" [inlineStyles] [linkUnderlineToggle]);
 * what differs between the texts is here: the toolbar, the least height (headers are one line
 * high), and whether plain text keeps line breaks.
 */
export interface WidgetTextEditorConfig {
  tools: RichTextTool[];
  height: number;
  multiline: boolean;
}

export const WIDGET_TEXT_EDITOR_TOOLS: RichTextTool[] = toolsFromToolbarGroups([
  {items: ['Undo', 'Redo']},
  {items: ['Bold', 'Italic', 'Underline']},
  {items: ['FontSize']},
  {items: ['TextColor']},
  {items: ['Link']},
]);

export const WIDGET_TEXT_EDITOR_CONFIG: WidgetTextEditorConfig = {
  tools: WIDGET_TEXT_EDITOR_TOOLS,
  height: 110,
  multiline: true,
};

export const WIDGET_HEADER_TEXT_EDITOR_CONFIG: WidgetTextEditorConfig = {
  ...WIDGET_TEXT_EDITOR_CONFIG,
  height: 64,
  multiline: false,
};

export type WidgetHeaderKey = 'header_1' | 'header_2' | 'header_3';

/** Where the widget shows each header, as every page that edits them says it. */
export const WIDGET_HEADER_DESCRIPTIONS: Record<WidgetHeaderKey, string> = {
  header_1: 'The line across the top of the widget, hidden when empty',
  header_2: 'The big headline above the share buttons',
  header_3: 'The smaller text under the headline',
};

// What the widget shows for a header with no text anywhere (Influencer.service). Header 1 has none:
// the widget leaves it out.
const WIDGET_HEADER_BUILT_IN_TEXTS: Partial<Record<WidgetHeaderKey, string>> = {
  header_2: 'Invite someone!',
  header_3: 'Click to share with your community',
};

/**
 * What an empty header box shows in grey: what the widget renders for it, given the text it
 * inherits (none when the box does not inherit). Only a hint: it is never written to the field.
 */
export function widgetHeaderPlaceholder(key: WidgetHeaderKey, inheritedText?: string | null): string {
  return inheritedText || WIDGET_HEADER_BUILT_IN_TEXTS[key] || 'Not shown in the widget';
}

/** For a box that keeps its own text while Use Global is on: the widget does not show that text. */
export function widgetHeaderNotLiveNote(key: WidgetHeaderKey, globalText?: string | null): {label: string; text?: string} {
  if (globalText) {
    return {label: 'Not live. The widget is using the global text:', text: globalText};
  }
  const builtIn = WIDGET_HEADER_BUILT_IN_TEXTS[key];
  return builtIn
    ? {label: 'Not live. The widget is using its default text:', text: builtIn}
    : {label: 'Not live. The widget does not show this line.'};
}
